import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiResponse } from 'app/core/models/date-range-preset.models';
import { withIdempotencyKey } from 'app/core/utils/idempotency.util';
import { environment } from 'environments/environment';
import {
  CreateDiCustomerRequest,
  CreateDiInvoiceRequest,
  CreateDiProductRequest,
  DiBuyerRegisterRow,
  DiCustomer,
  DiDashboard,
  DiDashboardQuery,
  DiFailedTransmissionRow,
  DiFbrScenario,
  DiInvoice,
  DiInvoiceListItem,
  DiInvoiceListQuery,
  DiListQuery,
  DiLiveHsUomQuery,
  DiLiveRateQuery,
  DiLiveSroItemQuery,
  DiLiveSroScheduleQuery,
  DiPrint,
  DiProduct,
  DiReferenceItem,
  DiReferenceKindParam,
  DiReportQuery,
  DiSettings,
  DiStatl,
  DiTaxSummaryRow,
  DocumentAuditTimelineItem,
  UpsertDiSettingsRequest,
} from './di.models';
import { DiReferenceKind } from './di.constants';

function toHttpParams(
  query: object | Record<string, string | number | boolean | null | undefined> | undefined
): HttpParams | undefined {
  if (!query) return undefined;
  let params = new HttpParams();
  let hasAny = false;
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    params = params.set(key, String(value));
    hasAny = true;
  }
  return hasAny ? params : undefined;
}

function asTrimmedString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function pickRecordValue(rec: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const text = asTrimmedString(rec[key]);
    if (text) return text;
  }
  return null;
}

function unwrapReferenceList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const rec = payload as Record<string, unknown>;
  const nested = rec['data'] ?? rec['Data'] ?? rec['items'] ?? rec['Items'];
  if (Array.isArray(nested)) return nested;
  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>;
    const list = inner['items'] ?? inner['Items'] ?? inner['data'] ?? inner['Data'];
    if (Array.isArray(list)) return list;
  }
  return [];
}

function normalizeReferenceItem(raw: unknown): DiReferenceItem {
  const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const name = pickRecordValue(rec, [
    'name',
    'Name',
    'description',
    'Description',
    'uomDesc',
    'uom_desc',
    'uom',
    'Uom',
    'uoM',
  ]);
  const code = pickRecordValue(rec, [
    'code',
    'Code',
    'externalKey',
    'ExternalKey',
    'uoM_ID',
    'uom_ID',
    'uomId',
    'UomId',
  ]);
  const label = name || code;
  return {
    id: pickRecordValue(rec, ['id', 'Id']) || code || label || '',
    kind: pickRecordValue(rec, ['kind', 'Kind']) || '',
    externalKey: pickRecordValue(rec, ['externalKey', 'ExternalKey']) || code,
    code: code || label,
    name: label,
    syncedAt: pickRecordValue(rec, ['syncedAt', 'SyncedAt']) || '',
  };
}

@Injectable({ providedIn: 'root' })
export class DiApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}di`;

  // ─── Settings ─────────────────────────────────────────────────────────────

  getSettings(): Observable<ApiResponse<DiSettings>> {
    return this.http.get<ApiResponse<DiSettings>>(`${this.base}/settings`);
  }

  upsertSettings(
    body: UpsertDiSettingsRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiSettings>> {
    return this.http.put<ApiResponse<DiSettings>>(
      `${this.base}/settings`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  // ─── Customers ────────────────────────────────────────────────────────────

  getCustomers(query?: DiListQuery): Observable<ApiResponse<DiCustomer[]>> {
    return this.http.get<ApiResponse<DiCustomer[]>>(`${this.base}/customers`, {
      params: toHttpParams(query),
    });
  }

  getCustomer(id: string): Observable<ApiResponse<DiCustomer>> {
    return this.http.get<ApiResponse<DiCustomer>>(`${this.base}/customers/${id}`);
  }

  createCustomer(
    body: CreateDiCustomerRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiCustomer>> {
    return this.http.post<ApiResponse<DiCustomer>>(
      `${this.base}/customers`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  updateCustomer(
    id: string,
    body: CreateDiCustomerRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiCustomer>> {
    return this.http.put<ApiResponse<DiCustomer>>(
      `${this.base}/customers/${id}`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  deactivateCustomer(id: string, idempotencyKey?: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.base}/customers/${id}/deactivate`,
      null,
      withIdempotencyKey(idempotencyKey)
    );
  }

  // ─── Products ─────────────────────────────────────────────────────────────

  getProducts(query?: DiListQuery): Observable<ApiResponse<DiProduct[]>> {
    return this.http.get<ApiResponse<DiProduct[]>>(`${this.base}/products`, {
      params: toHttpParams(query),
    });
  }

  getProduct(id: string): Observable<ApiResponse<DiProduct>> {
    return this.http.get<ApiResponse<DiProduct>>(`${this.base}/products/${id}`);
  }

  createProduct(
    body: CreateDiProductRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiProduct>> {
    return this.http.post<ApiResponse<DiProduct>>(
      `${this.base}/products`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  updateProduct(
    id: string,
    body: CreateDiProductRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiProduct>> {
    return this.http.put<ApiResponse<DiProduct>>(
      `${this.base}/products/${id}`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  deactivateProduct(id: string, idempotencyKey?: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.base}/products/${id}/deactivate`,
      null,
      withIdempotencyKey(idempotencyKey)
    );
  }

  // ─── Invoices ─────────────────────────────────────────────────────────────

  getInvoices(query?: DiInvoiceListQuery): Observable<ApiResponse<DiInvoiceListItem[]>> {
    return this.http.get<ApiResponse<DiInvoiceListItem[]>>(`${this.base}/invoices`, {
      params: toHttpParams(query),
    });
  }

  getInvoice(id: string): Observable<ApiResponse<DiInvoice>> {
    return this.http.get<ApiResponse<DiInvoice>>(`${this.base}/invoices/${id}`);
  }

  createInvoice(
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiInvoice>> {
    return this.http.post<ApiResponse<DiInvoice>>(
      `${this.base}/invoices`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  updateInvoice(
    id: string,
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiInvoice>> {
    return this.http.put<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/${id}`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Create → Validate (same body as create). */
  createValidateInvoice(
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiInvoice>> {
    return this.http.post<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/create-validate`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Update draft → Validate (same body as update). */
  updateValidateInvoice(
    id: string,
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiInvoice>> {
    return this.http.put<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/${id}/create-validate`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Create → Submit (validation optional on BE). */
  createSubmitInvoice(
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiInvoice>> {
    return this.http.post<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/create-submit`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Update draft → Submit. */
  updateSubmitInvoice(
    id: string,
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiInvoice>> {
    return this.http.put<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/${id}/create-submit`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Create draft → DiPrint (no FBR/PRA call). */
  createPrintInvoice(
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiPrint>> {
    return this.http.post<ApiResponse<DiPrint>>(
      `${this.base}/invoices/create-print`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  /** Update draft → DiPrint (no FBR/PRA call). */
  updatePrintInvoice(
    id: string,
    body: CreateDiInvoiceRequest,
    idempotencyKey?: string
  ): Observable<ApiResponse<DiPrint>> {
    return this.http.put<ApiResponse<DiPrint>>(
      `${this.base}/invoices/${id}/create-print`,
      body,
      withIdempotencyKey(idempotencyKey)
    );
  }

  validateInvoice(id: string, idempotencyKey?: string): Observable<ApiResponse<DiInvoice>> {
    return this.http.post<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/${id}/validate`,
      null,
      withIdempotencyKey(idempotencyKey)
    );
  }

  submitInvoice(id: string, idempotencyKey?: string): Observable<ApiResponse<DiInvoice>> {
    return this.http.post<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/${id}/submit`,
      null,
      withIdempotencyKey(idempotencyKey)
    );
  }

  cancelInvoice(id: string, idempotencyKey?: string): Observable<ApiResponse<DiInvoice>> {
    return this.http.post<ApiResponse<DiInvoice>>(
      `${this.base}/invoices/${id}/cancel`,
      null,
      withIdempotencyKey(idempotencyKey)
    );
  }

  getInvoicePrint(id: string): Observable<ApiResponse<DiPrint>> {
    return this.http.get<ApiResponse<DiPrint>>(`${this.base}/invoices/${id}/print`);
  }

  getInvoiceAuditHistory(
    id: string
  ): Observable<ApiResponse<DocumentAuditTimelineItem[]>> {
    return this.http.get<ApiResponse<DocumentAuditTimelineItem[]>>(
      `${this.base}/invoices/${id}/audit-history`
    );
  }

  // ─── PRA ──────────────────────────────────────────────────────────────────

  getPraInvoices(query?: DiInvoiceListQuery): Observable<ApiResponse<DiInvoiceListItem[]>> {
    return this.http.get<ApiResponse<DiInvoiceListItem[]>>(`${this.base}/pra-invoices`, {
      params: toHttpParams(query),
    });
  }

  // ─── Dashboard ────────────────────────────────────────────────────────────

  getDashboard(query: DiDashboardQuery): Observable<ApiResponse<DiDashboard>> {
    return this.http.get<ApiResponse<DiDashboard>>(`${this.base}/dashboard`, {
      params: toHttpParams(query),
    });
  }

  // ─── Reports ──────────────────────────────────────────────────────────────

  getInvoiceRegister(
    query?: DiReportQuery
  ): Observable<ApiResponse<DiInvoiceListItem[]>> {
    return this.http.get<ApiResponse<DiInvoiceListItem[]>>(
      `${this.base}/reports/invoice-register`,
      { params: toHttpParams(query) }
    );
  }

  getPraSubmittedReport(
    query?: DiReportQuery
  ): Observable<ApiResponse<DiInvoiceListItem[]>> {
    return this.http.get<ApiResponse<DiInvoiceListItem[]>>(
      `${this.base}/reports/pra-submitted`,
      { params: toHttpParams(query) }
    );
  }

  getTaxSummary(query?: DiReportQuery): Observable<ApiResponse<DiTaxSummaryRow[]>> {
    return this.http.get<ApiResponse<DiTaxSummaryRow[]>>(
      `${this.base}/reports/tax-summary`,
      { params: toHttpParams(query) }
    );
  }

  getBuyerRegister(
    query?: DiReportQuery
  ): Observable<ApiResponse<DiBuyerRegisterRow[]>> {
    return this.http.get<ApiResponse<DiBuyerRegisterRow[]>>(
      `${this.base}/reports/buyer-register`,
      { params: toHttpParams(query) }
    );
  }

  getFailedTransmissions(
    query?: DiReportQuery
  ): Observable<ApiResponse<DiFailedTransmissionRow[]>> {
    return this.http.get<ApiResponse<DiFailedTransmissionRow[]>>(
      `${this.base}/reports/failed-transmissions`,
      { params: toHttpParams(query) }
    );
  }

  downloadReportCsv(
    report:
      | 'invoice-register'
      | 'pra-submitted'
      | 'tax-summary'
      | 'buyer-register'
      | 'failed-transmissions',
    query?: DiReportQuery
  ): Observable<Blob> {
    return this.http.get(`${this.base}/reports/${report}`, {
      params: toHttpParams({ ...query, csv: true }),
      responseType: 'blob',
    });
  }

  // ─── Reference ────────────────────────────────────────────────────────────

  getReference(kind: DiReferenceKindParam): Observable<ApiResponse<DiReferenceItem[]>> {
    return this.http.get<ApiResponse<DiReferenceItem[] | unknown>>(`${this.base}/reference/${kind}`).pipe(
      map((resp) => {
        const items = unwrapReferenceList(resp).map(normalizeReferenceItem);
        return { ...resp, data: items };
      })
    );
  }

  syncReference(
    kind: DiReferenceKindParam,
    idempotencyKeyOrQuery?: string | Record<string, string | number | boolean | null | undefined>,
    idempotencyKey?: string
  ): Observable<ApiResponse<number>> {
    const query =
      idempotencyKeyOrQuery && typeof idempotencyKeyOrQuery === 'object'
        ? idempotencyKeyOrQuery
        : undefined;
    const key =
      typeof idempotencyKeyOrQuery === 'string' ? idempotencyKeyOrQuery : idempotencyKey;
    return this.http.post<ApiResponse<number>>(
      `${this.base}/reference/${kind}/sync`,
      null,
      { ...withIdempotencyKey(key), params: toHttpParams(query) }
    );
  }

  getStatl(regno: string, date?: string): Observable<ApiResponse<DiStatl>> {
    return this.http.get<ApiResponse<DiStatl>>(`${this.base}/reference/statl`, {
      params: toHttpParams({ regno, date }),
    });
  }

  getRegistrationType(registrationNo: string): Observable<ApiResponse<DiStatl>> {
    return this.http.get<ApiResponse<DiStatl>>(`${this.base}/reference/registration-type`, {
      params: toHttpParams({ registrationNo }),
    });
  }

  /** GET /di/reference/scenarios — SN001–SN028 metadata */
  getScenarios(): Observable<ApiResponse<DiFbrScenario[]>> {
    return this.http.get<ApiResponse<DiFbrScenario[] | unknown>>(`${this.base}/reference/scenarios`).pipe(
      map((resp) => {
        const items = unwrapReferenceList(resp).map(normalizeScenario);
        return { ...resp, data: items };
      })
    );
  }

  getLiveReference(
    kind: DiReferenceKindParam,
    query: Record<string, string | number | boolean | null | undefined>
  ): Observable<ApiResponse<DiReferenceItem[]>> {
    return this.http
      .get<ApiResponse<DiReferenceItem[] | unknown>>(`${this.base}/reference/${kind}/live`, {
        params: toHttpParams(query),
      })
      .pipe(
        map((resp) => {
          const items = unwrapReferenceList(resp).map(normalizeReferenceItem);
          return { ...resp, data: items };
        })
      );
  }

  getRatesLive(query: DiLiveRateQuery): Observable<ApiResponse<DiReferenceItem[]>> {
    return this.getLiveReference(DiReferenceKind.Rate, {
      date: query.date,
      transTypeId: query.transTypeId,
      originationSupplier: query.originationSupplier,
    });
  }

  getSroSchedulesLive(query: DiLiveSroScheduleQuery): Observable<ApiResponse<DiReferenceItem[]>> {
    return this.getLiveReference(DiReferenceKind.SroSchedule, {
      date: query.date,
      rateId: query.rateId,
    });
  }

  getSroItemsLive(query: DiLiveSroItemQuery): Observable<ApiResponse<DiReferenceItem[]>> {
    return this.getLiveReference(DiReferenceKind.SroItem, {
      date: query.date,
      sroId: query.sroId,
    });
  }

  /**
   * Live FBR HS ↔ UoM lookup. Kind path is case-sensitive: `HsUom`.
   * GET /di/reference/HsUom/live?hsCode=…&annexureId=3
   */
  getHsUoms(
    hsCode: string,
    annexureId = 3
  ): Observable<ApiResponse<DiReferenceItem[]>> {
    const query: DiLiveHsUomQuery = { hsCode, annexureId };
    return this.getLiveReference(DiReferenceKind.HsUom, {
      hsCode: query.hsCode,
      annexureId: query.annexureId ?? 3,
    });
  }
}

function normalizeScenario(raw: unknown): DiFbrScenario {
  const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const bool = (keys: string[], fallback = false): boolean => {
    for (const k of keys) {
      const v = rec[k];
      if (typeof v === 'boolean') return v;
      if (v === 'true' || v === 1 || v === '1') return true;
      if (v === 'false' || v === 0 || v === '0') return false;
    }
    return fallback;
  };
  return {
    scenarioId: asTrimmedString(rec['scenarioId'] ?? rec['ScenarioId']) || '',
    title: asTrimmedString(rec['title'] ?? rec['Title']) || '',
    saleType: asTrimmedString(rec['saleType'] ?? rec['SaleType']) || '',
    sampleRate: asTrimmedString(rec['sampleRate'] ?? rec['SampleRate']) || '',
    requiresSro: bool(['requiresSro', 'RequiresSro']),
    requiresMrp: bool(['requiresMrp', 'RequiresMrp']),
    emptyExtraTax: bool(['emptyExtraTax', 'EmptyExtraTax']),
    buyerRegistrationHint:
      asTrimmedString(rec['buyerRegistrationHint'] ?? rec['BuyerRegistrationHint']) || 'Either',
    notes: asTrimmedString(rec['notes'] ?? rec['Notes']) || '',
  };
}
